import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { SharedComponents } from "../../../components/shared.module";
import { HasRoleDirective } from "../../../core/directives/has-role.directive";
import { GroupedBarChartConfig } from "../../../components/chart/grouped-bar-chart/grouped-bar-chart.component";
import { MultiLineChartConfig } from "../../../components/chart/multi-line-chart/multi-line-chart.component";
import { NgbCarousel, NgbCarouselModule, NgbModal, NgbSlideEvent } from "@ng-bootstrap/ng-bootstrap";
import { ProjectConfigurationComponent } from "../../../components/modal-templates/project-configuration/project-configuration.component";
import { SolutionModalComponentComponent } from "../../../components/modal-templates/solution-modal-component/solution-modal-component.component";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";
import { ExpandableAccordionComponent } from "../../../components/common/expandable-accordion/expandable-accordion.component";

/**
 * The `OverviewComponent` provides an overview of various sections, such as Design, Execution,
 * Analytics, and Maintenance, with their respective components. It allows toggling the expanded
 * state of each section and navigating to a questionnaire survey page.
 */
@Component({
  selector: "app-overview",
  standalone: true,
  imports: [CommonModule, SharedComponents, HasRoleDirective, ExpandableAccordionComponent, NgbCarouselModule],
  templateUrl: "./overview.component.html",
  styleUrl: "./overview.component.scss",
})
export class OverviewComponent extends BaseLayoutComponent implements OnInit {
  @ViewChild("carousel") carousel?: NgbCarousel;

  slides = [
    "assets/images/virtusa-icons/frame1.svg",
    "assets/images/virtusa-icons/frame2.svg",
    "assets/images/virtusa-icons/frame3.svg",
    "assets/images/virtusa-icons/frame4.svg",
  ];

  overview = [
    {
      name: "Design",
      components: [
        { name: "Test Data Generator", description: 'Automated generation of synthetic test   data aligned for different test scenarios.', image: 'tdg.svg' },
        { name: "Test Script Generator", description: 'Automates and expedites test script   creation and framework generation.', image: 'tsg.svg' },
        { name: "Test Case Generator", description: 'Automated generation of test cases from   user stories/requirements.', image: 'tcg.svg' }
      ]
    },
    {
      name: "Execution",
      components: [
        { name: "Test Execution Management & Execution Engines", description: 'Enables efficient test planning and   execution, utilizing test execution engines of choice.', image: 'tem.svg' },
        { name: "Test Execution Report", description: 'Provides test execution status and   corresponding details in near real time.', image: 'ter.svg' }
      ]
    },
    {
      name: "Analytics",
      components: [
        { name: "Test Report Summarizer", description: 'Aggregates test execution results over time and provides intelligent insights on the application’s functional health based on test artifacts and defects linked to executions.', image: 'trs.svg' },
        { name: "Test Failure Analyzer", description: 'Automated root cause analysis of test   execution failures and auto suggests possible resolutions.', image: 'tfa.svg' }
      ]
    },
    {
      name: "Maintenance",
      components: [
        { name: "High Impacted Test Cases", description: 'Automatic detection of functional coverage, including missing test cases and high priority test cases, comparing source code with the test suite.', image: 'highimpact.svg' },
        { name: "Test Case Optimizer", description: 'Automated test suite rationalization   & optimization', image: 'tco.svg' },
        { name: "Self Healing", description: 'Automatic resolution of UI locator   mismatches during test execution to prevent script failures.', image: 'sh.svg' }
      ]
    }
  ];

  currentExpandedSection: string | null = this.overview[0]?.name;
  isAlreadySubmitted: any[] = [{}];

  codeReviewData = {
    columns: [
      { mapping_key: 'testCaseId', display_name: 'ID', isVisible: true },
      { mapping_key: 'testCase', display_name: 'File', isVisible: true },
    ],
    rows: [
      {
        testCaseId: 'CS0009',
        testCase: 'src/main/avacomaflexbanking/repositories/TransactionRepository.java',
        testCases: [
          { id: 'TC 1', description: 'Test the getUserName method with a valid input and verify if it returns the correct userName.' },
          { id: 'TC 2', description: 'Test the getUserName method with an invalid input and ensure it throws the appropriate error.' },
          { id: 'TC 3', description: 'Test the getUserName method with a null input and check if it handles the case gracefully.' }
        ],
        codeSnippet: `public Transaction getTransactionById(Long id) {\n    return transactionRepository.findById(id).orElse(null);\n}`
      },
      {
        testCaseId: 'CS0010',
        testCase: 'src/main/avacomaflexbanking/models/Account.java',
        testCases: [
          { id: 'TC 1', description: 'Test the getAccountBalance method with a valid account ID and verify it returns the correct balance.' },
          { id: 'TC 2', description: 'Test the getAccountBalance method with an inactive account and ensure it returns a zero balance.' }
        ],
        codeSnippet: `public Double getAccountBalance(Long accountId) {\n    Account account = accountRepository.findById(accountId).orElseThrow();\n    return account.getBalance();\n}`
      },
      {
        testCaseId: 'CS0011',
        testCase: 'src/main/avacomaflexbanking/services/PaymentService.java',
        testCases: [
          { id: 'TC 1', description: 'Test the processPayment method with sufficient funds and verify the transaction is completed successfully.' },
          { id: 'TC 2', description: 'Test the processPayment method with insufficient funds and confirm it throws an InsufficientFundsException.' },
          { id: 'TC 3', description: 'Test the processPayment method with a negative amount and ensure it throws an InvalidAmountException.' },
          { id: 'TC 4', description: 'Test the processPayment method with a null recipient account and verify proper error handling.' }
        ],
        codeSnippet: `public PaymentResult processPayment(PaymentRequest request) {\n    validateRequest(request);\n    Account source = accountRepository.findById(request.getSourceId()).orElseThrow();\n    debit(source, request.getAmount());\n    return new PaymentResult(true);\n}`
      }
    ]
  };

  weeklyChartConfig: GroupedBarChartConfig = {
    currencyPrefix: '$',
    labels: [
      ['MON', '(16 APR)'],
      ['TUE', '(17 APR)'],
      ['WED', '(18 APR)'],
      ['THU', '(19 APR)'],
      ['FRI', '(20 APR)'],
      ['SAT', '(21 APR)'],
      ['SUN', '(22 APR)'],
    ],
    datasets: [
      { label: 'Design',                data: [25000,  70000, 75000,  75000,  10000,  30000, 50000] },
      { label: 'Execution',             data: [100000, 25000, 50000, 55000,  15000,  150000, 190000] },
      { label: 'Reporting & Analytics', data: [110000, 10000, 10000, 110000, 10000, 10000, 10000] },
      { label: 'Maintenance',           data: [25000, 130000, 110000, 30000,  30000,  90000,  95000 ] },
    ],
  };

  tokenUsageConfig: MultiLineChartConfig = {
    valueUnit: 'Tokens',
    labels: [
      ['MON', '(16 APR)'],
      ['TUE', '(17 APR)'],
      ['WED', '(18 APR)'],
      ['THU', '(19 APR)'],
      ['FRI', '(20 APR)'],
      ['SAT', '(21 APR)'],
      ['SUN', '(22 APR)'],
    ],
    datasets: [
      { label: 'Design',                 data: [50000,  120000, 90000,  75000,  60000,  30000,  80000] },
      { label: 'Execution',              data: [80000,  200000, 150000, 180000, 100000, 50000,  130000] },
      { label: 'Reporting & Analytics',  data: [30000,  500000, 220000, 300000, 150000, 80000,  200000] },
      { label: 'Maintenance',            data: [10000,  80000,  60000,  50000,  40000,  20000,  70000] },
    ],
  };

  private modalService = inject(NgbModal);

  ngOnInit(): void {
    this.getSelectedApproach();
  }

  loadProjectConfigs() {
    const modalRef = this.modalService.open(ProjectConfigurationComponent, {
      centered: true,
      windowClass: "scrip-mapping-modal",
      backdrop: 'static',
    });
    if (this.dataStore?.data?.id) {
      modalRef.componentInstance.data = this.dataStore?.data?.id;
    }
    modalRef.result.then((data: any) => { });
  }

  // Toggle the expanded state for a given section and sync the carousel slide
  toggleSection(name: string) {
    this.currentExpandedSection = name;
    this.carousel?.select(name);
  }

  onCarouselSlide(event: NgbSlideEvent) {
    if (this.overview.some((section) => section.name === event.current)) {
      this.currentExpandedSection = event.current;
    }
  }

  /**
   * Checks if a given section is currently expanded.
   * @param name - The name of the section to check.
   * @returns True if the section is expanded; otherwise, false.
   */
  isExpanded(name: string): boolean {
    return this.currentExpandedSection === name;
  }

  /**
   * Navigates to the "Questionnaire Survey" page for the current project.
   * Logs the current data store to the console before navigation.
   */
  organizeYourAssets() {
    console.log(this.dataStore);
    const data = this.dataStore?.data;
    this.router.navigate([
      "/project",
      data?.id,
      data?.name,
      "questionnaire-survey",
    ]);
  }

  openSolutionSelectPopup() {
    const modalRef = this.modalService.open(SolutionModalComponentComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    modalRef.componentInstance.projectId = this.dataStore?.data?.id;
    modalRef.result.then((result) => {
      console.log(result);
      if (result !== 'agentic') {
        this.verifyAndPopConfigModal();
      }
      let approach = result === 'agentic' ? "AGENTIC_AI" : "NON_AGENTIC_AI";
      this.restService.aiApproach(this.dataStore?.data?.id, approach)
        .subscribe({
          next: (res: any) => {
            console.log('AI Approach set to :', res);
            this.toaster.pop({
              type: "success",
              text: res?.data?.aiApproach === "AGENTIC_AI" ? MessageEnum.AI_APPROACH_SET_TO_AGENTIC : MessageEnum.AI_APPROACH_SET_TO_NON_AGENTIC,
            });
            this.agenticAiService.setAiApproach(res?.data?.aiApproach);
            if (res?.data?.aiApproach === "AGENTIC_AI") {
              window.location.reload();
            }
          },
          error: (err) => {
            console.error('Error selecting ai approach:', err);
            this.toaster.pop({
              type: "error",
              text: MessageEnum.AI_APPROACH_SET_FAILED,
            });
          }
        });
    });
  }

  getSelectedApproach() {
    const projectId = this.dataStore?.data?.id;
    if (!projectId) {
      return;
    }

    this.restService.getSelectedAiApproach(projectId).subscribe({
      next: (res: any) => {
        const approach = res?.data?.aiApproach;
        if (approach) {
          this.agenticAiService.setAiApproach(approach);
          if (res?.data?.isAgenticEnabled !== undefined) {
            this.agenticAiService.setIsAgenticApproach(res.data.isAgenticEnabled);
          }
          this.verifyAndPopConfigModal();
          return;
        }
        this.openSolutionSelectPopup();
      },
      error: (err) => {
        const status = err?.status ?? err?.error?.status;
        const message = `${err?.error?.message ?? err?.message ?? ""}`;
        const approachNotSet = message
          .toLowerCase()
          .includes("ai approach not set");
        if (status === 400 || approachNotSet) {
          this.openSolutionSelectPopup();
        }
      },
    });
  }

  verifyAndPopConfigModal() {
    this.restService
      .getSurveySubmission(this.dataStore?.data?.id)
      .subscribe((item) => {
        const answers = item?.data?.answers;
        this.isAlreadySubmitted = answers?.filter((item: any) => item?.answer);

        if (!this.isAlreadySubmitted.length) {
          this.loadProjectConfigs();
        }
      });
  }
}
